/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 *
 */
import { Osf, Sensor } from '@ouster/js-osf';
import { DataMessage } from '@ouster/webviz';
import {
  CircularProgress,
  PlayBar,
  ReplayStreamInfo,
  SensorSectionInfo,
  VizLeftSidebar,
  Flex,
  Button,
  Icon,
  SensorInfo,
  SensorConfig,
  ShortcutKeyHandler,
  Box,
  VizFooter,
  ModalContainer,
  useDisclosure,
} from '@ouster/webviz-react-components';
import type {
  ImageSettings,
  FilesInfo,
  CreatedOption,
  PointCloudSensorSettings,
} from '@ouster/webviz-react-components';

import { isEmpty, isUndefined, throttle, uniq, isEqual } from 'lodash';
import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  ComponentProps,
  useMemo,
} from 'react';
import { isMacOs } from 'react-device-detect';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Subject } from 'rxjs';
import { frameCache } from 'src/reducers/vizScene';
import styled from 'styled-components/macro';

import { ContainerBelowNav } from './NavBar';
import { VizPage, VizPageRef } from './VizPage';
import { DriveNavBar } from '../components/DriveNavBar';
import { Toast } from '../components/Toast';
import { getOSFWorker } from '../getOSFWorker';
import useChakraHook from '../hooks/useChakraHook';
import { useSentryErrorTracker } from '../hooks/useSentryErrorTracker';
import { useStoreState, useStoreActions } from '../reducers';
import { DriveOsfInfo, getSectionIdFromGlobalFrame } from '../reducers/drive'; // NOTE: sectionId has replaced bagId
// import { colorModeName } from '../reducers/vizScene';
import { Tag, MappedTag, OSFType, Status } from '../types';
import { GCS_BASE_URL } from '../utils/constants';
import {
  usePrevious,
  useAnimationFrameThrottled,
  useFeedbackForm,
} from '../utils/customHooks';
import { mod } from '../utils/math';
import { formatTag, mapData, debounce } from '../utils/misc';
import { getSignedUrl } from '../utils/requests';
import { updateFrameInUrl } from '../utils/searchParams';
import { createDriveViewerShortcuts } from '../utils/shortcuts';

// How often we check on state of the processes and reload [ms]
const DRIVE_REFRESH_DELAY = 5000;

const LoaderContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  position: fixed;
  top: 0;
  background-color: var(--color-unify-darker-blue);
`;

const TopLevel = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
  flex-direction: column;
  background-color: #22272d;
  color: #ffffff;
`;

const FeedbackFormContainer = styled.a.attrs<{
  formLink: string;
}>((props) => ({
  href: props.formLink,
  target: '_blank', // Open form in separate window
  rel: 'noopener noreferrer',
}))<{
  formLink: string;
}>`
  /* semantic ui pollutes our CSS here so we override the color for
  default and hover state of our link */
  &,
  &:hover {
    color: ${(props) => props.theme.primaryColor};
  }

  &:hover {
    filter: brightness(130%);
  }
`;

const VizInfoMessageContainer = styled.div`
  /* Center the div horizontally within the top level flexbox */
  align-self: center;

  /* Keep the text to a maximum of 60ch for readibility*/
  max-width: 68ch;
  /* Same padding as Navbar */
  padding: 56px;
  height: 100%;

  /* Font settings which stand out */
  font-weight: 500;
  font-size: 18px;
  line-height: 1.8;

  /* Center the text within the div */
  display: grid;
  place-items: center;

  /* Allow for line breaks */
  white-space: pre-wrap;

  em {
    color: var(--color-nebula-yellow);
  }
`;

const SliderControlsContainer = styled.div`
  /* The playbar component from webviz-react-components library has a
  height of 64px so we force our container here to be 64px high */
  flex: 0 0 64px;

  // Copy background color from playbar container so the extra padding
  // has the same color as content
  background-color: #313841;

  display: flex;
  flex-direction: row;
`;

// When the user drags the playbar thumb, we want to update the frame
// index in the URL
const onSliderChange = throttle((values: ReadonlyArray<number>) => {
  const sliderValue = Math.floor(values[0]);
  if (sliderValue === undefined || Number.isNaN(sliderValue)) {
    console.warn(`Slider value has to be a number, got ${sliderValue}`);
  } else {
    updateFrameInUrl(sliderValue);
  }
}, 75);

const osfTypeFromString = (osfConfigMode: string) => {
  switch (osfConfigMode) {
    case 'osf':
      return OSFType.legacyOsf;
    case 'binary-osf':
      return OSFType.osf;
    default:
      throw new Error(`OSF mode ${osfConfigMode} is not supported`);
  }
};

const extractOSFType = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfTypeFromString(osfInfo.configById[sectionId].mode);

const extractOSFPath = (osfInfo: DriveOsfInfo, sectionId: string) =>
  `${sectionId}/${osfInfo.configById[sectionId].dataPath}/${sectionId}.osf`;

const extractOSFOffset = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfInfo.offsetsById[sectionId];

// Note that in the current 'old' web-viz, the data loader and its
// frame manager (which eventually controls the 'loadedLocalFrames') only
// deal with a single OSF file at a time, so they won't buffer data
// between multiple files, which means that there will be no buffering
// at the junction of files
const getMaxLoadedLocalFrame = (
  currentLocalFrame: number,
  loadedLocalFrames: number[]
) => {
  let nextConsecutive = 0;
  if (loadedLocalFrames && loadedLocalFrames.length > 0) {
    // Get max index for which every number exists between min and max
    nextConsecutive = currentLocalFrame;
    const maxInd = Math.max(...loadedLocalFrames);
    while (nextConsecutive < maxInd) {
      if (!loadedLocalFrames.includes(nextConsecutive)) {
        break;
      }
      nextConsecutive += 1;
    }
  }
  return nextConsecutive;
};

/* 
  Help method to adjust frame rate based take image number and resolution into consideration
*/
const calculateAdjustedFrameRate = (
  baseFrameRate: number,
  imageCount: number,
  resolution: number | undefined,
  speed: number
): number => {
  if (!resolution) return baseFrameRate * speed;

  const baseResolution = 1024;

  // Resolution factor based on a progressive scaling with a cap
  const resolutionFactor =
    resolution > baseResolution
      ? 1 + Math.log2(resolution / baseResolution) * 0.15
      : 1;

  // Image factor with a more conservative scaling
  const imageFactor = 1.2 ** (imageCount - 1);

  // Speed factor: progressively scales, capped at a maximum multiplier of 4
  // we usually do good on speed 1, but when the number go up
  // it seems we need to increase the speed to hit the desired number
  const speedFactor = Math.min(speed * Math.sqrt(speed) * resolutionFactor, 4);

  // Calculate adjusted frame rate using combined factor scaling
  const adjustedFrameRate =
    baseFrameRate * resolutionFactor * imageFactor * speedFactor;
  return Math.round(adjustedFrameRate);
};

const DriveViewer = React.memo(() => {
  const { showToast } = useChakraHook();
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const vizPageRef = useRef<VizPageRef>(null);
  // Local State
  const [frameTs, setFrameTs] = useState<number | undefined>(undefined);
  const [loadedLocalFrames, setLoadedLocalFrames] = useState<number[]>([]);
  const [hoverMode, setHoverMode] = useState(false);
  const [isVizInitialized, setIsVizInitialized] = useState(false);
  // const [shouldZoomIn, setShouldZoomIn] = useState(false);
  // const [shouldZoomOut, setShouldZoomOut] = useState(false);
  const [replayFPS, setReplayFPS] = useState(10); // Default 10hz until we get drive Info object

  // Grab information from the URL
  const { orgId, driveId, publishedDriveUuid } = useParams<{
    orgId?: string;
    driveId?: string;
    publishedDriveUuid?: string;
  }>();
  const [searchParams] = useSearchParams();
  const routeFrameIdxString = searchParams.get('frame');
  const routeFrameIdx = routeFrameIdxString ? +routeFrameIdxString : undefined;

  // Reducer state
  const {
    loadStatus,
    numFrames,
    currentFrameIdx,
    lastReceivedFrameIdx,
    seeking,
    frameStartIdx,
    driveInfo,
    driveOsfInfo,
    playing,
    playbackSpeed,
    playbackSpeedOptions,
    isSample,
    isPublished,
    sourceFileInfo,
    isShared,
  } = useStoreState((storeState) => ({ ...storeState.drive }));

  // fetch organizationId from driveInfo instead of searchParams
  const driveOrgId = Number(orgId) ?? 0;
  const filesToDownloadReady = useStoreState(
    (state) => state.drive.filesToDownloadReady
  );
  const outputProcesses = useStoreState((state) => state.drive.outputProcesses);

  const {
    pointSize,
    leftSidebarExpanded,
    flipped,
    pointCloudGlobalSettings,
    pointCloudSensorsSettings,
    twoDImageSettings,
    currentSensorImageSettings, // image settings for the current sensor
    is2DImageVisible,
    metadata,
    dataMessage,
    hasVizError,
    vizErrorMessage,
    availableChannels,
    rgbChannels,
  } = useStoreState((storeState) => ({ ...storeState.vizScene }));

  const {
    incrementPointSize,
    decrementPointSize,
    togglePointCloudAutoLevel,
    toggleCameraOrthographicMode,
    toggle2DImageVisibility,
    toggleAngleRulersVisibility,
    setLeftSidebarExpanded,
    setFlipped,
    updatePointCloudGlobalSettings,
    updatePointCloudSensorsSettings,
    updateDataMessage,
    togglePointCloudVisibility,
    updateMetadata,
    swapImages,
    addImage,
    deleteImage,
    updateImage,
    setHasVizError,
    setVizErrorMessage,
    setSelectedImagePanelSn,
    setShouldReplayCachedFrameOnRecreate,
  } = useStoreActions((actions) => actions.vizScene);

  const {
    setCurrentFrameIdx,
    setlastReceivedFrameIdx,
    loadDriveById,
    refreshDriveById,
    loadOutputFileInfo,
    loadSourceFileInfo,
    togglePause,
    setPlaybackSpeed,
    increasePlaybackSpeed,
    decreasePlaybackSpeed,
    setSeeking,
    setPlaying,
    setNumFrames,
    updateTags,
    setMappedTags,
  } = useStoreActions((actions) => actions.drive);
  // the imageSettings here represents all sensors image settings
  const { imageInfo, imageSettings, showImageRulers, selectedImagePanelSn } =
    twoDImageSettings;

  const { tags } = useStoreState((storeState) => ({
    ...storeState.recordingTag,
  }));

  const { fetchTags, createTag } = useStoreActions(
    (actions) => actions.recordingTag
  );

  const { curUserOrg, user, userOrgs } = useStoreState(
    (storeState) => storeState.user
  );
  const { fetchUserOrgs, setCurUserOrg } = useStoreActions(
    (actions) => actions.user
  );

  useEffect(() => {
    const getUserOrg = async () => {
      if (!user?.identity_id) return;
      try {
        await fetchUserOrgs(user.identity_id);
      } catch (error) {
        console.warn(error);
      }
    };
    getUserOrg();
  }, [driveOrgId, fetchUserOrgs, setCurUserOrg, user?.identity_id]);

  // TODO mw: Extract this logic to User store
  const isOusterUser = useMemo(() => {
    if (curUserOrg?.name === 'Ouster Inc') {
      return true;
    }
    return false;
  }, [curUserOrg]);

  // When the user changes the id in the URL, we don't want to have to
  // worry about resetting all the reducers state. The experience here
  // should be to start with a fresh page, so we just force a page
  // reload. If we wanted to make this process faster and not go through
  // login, we could try and be diligent about calling and tweaking
  // resetImmerState in all the appropriate reducers
  const previousDriveId = usePrevious(driveId);
  useEffect(() => {
    if (driveId && previousDriveId && driveId !== previousDriveId) {
      window.location.reload();
    }
  }, [driveId, previousDriveId]);
  const previousPublishedDriveUuid = usePrevious(publishedDriveUuid);

  useEffect(() => {
    if (
      publishedDriveUuid &&
      previousPublishedDriveUuid &&
      publishedDriveUuid !== previousPublishedDriveUuid
    ) {
      window.location.reload();
    }
  }, [publishedDriveUuid, previousPublishedDriveUuid]);

  // This starts loading a drive, and requests a list of source files.
  // Once the drive is loaded, state is updated and a rerender occurs
  // If the route was ../drive/..
  useEffect(() => {
    const loadDriveAndSource = async () => {
      try {
        if (driveId && !isUndefined(curUserOrg?.id)) {
          await loadDriveById({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
          await loadSourceFileInfo({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
        }
      } catch (error) {
        showToast({
          title: `Failed to download recording source file`,
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
      }
    };
    loadDriveAndSource();
  }, [
    driveId,
    loadDriveById,
    loadSourceFileInfo,
    driveOrgId,
    curUserOrg?.id,
    showToast,
  ]);

  // Fetch tags
  const disableTagRef = useRef(false);
  useEffect(() => {
    if (loadStatus !== 'success') return;

    if (userOrgs.some((org) => org.id === driveOrgId)) {
      if (!isShared) setCurUserOrg(driveOrgId);
      fetchTags(driveOrgId);
      disableTagRef.current = false;
    } else {
      disableTagRef.current = true;
    }
  }, [driveOrgId, fetchTags, setCurUserOrg, userOrgs, isShared, loadStatus]);

  // Tag objects
  const mappedTags: MappedTag[] = useMemo(() => mapData<Tag>(tags), [tags]);

  useEffect(() => {
    setMappedTags(mappedTags);
  }, [mappedTags, setMappedTags]);

  // keep the selected tags in the same order as the user selected
  // Map tag names in driveInfo to ids
  const mappedSelectedTags: MappedTag[] = useMemo(() => {
    if (!driveInfo) return [];
    const driveTags = driveInfo.tags;
    return mappedTags.filter((mappedTag) =>
      driveTags.includes(mappedTag.value)
    );
  }, [driveInfo, mappedTags]);

  // If the route was ../share/..
  useEffect(() => {
    if (publishedDriveUuid) {
      const loadPublishedDrive = async () => {
        try {
          await loadDriveById({
            id: publishedDriveUuid,
            isPrivate: false,
          });
          await loadSourceFileInfo({
            id: publishedDriveUuid,
            isPrivate: false,
          });
        } catch (error) {
          showToast({
            title: 'Failed to load published drive',
            description:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
            status: 'error',
          });
        }
      };
      loadPublishedDrive();
    }
  }, [publishedDriveUuid, loadDriveById, loadSourceFileInfo, showToast]);

  // Resolve lidar mode
  const lidarMode = useMemo(() => {
    const sdkLidarMode = driveInfo?.sensorInfos[0]?.sdkLidarMode;
    if (!metadata) return;
    const { configParams, lidarMode: mdLidarMode } = metadata;
    const cpLidarMode = configParams?.lidarMode;
    return cpLidarMode ?? sdkLidarMode ?? mdLidarMode; // Prefer config params lidar mode - some OSFs have an outermost lidar mode that is incorrect
  }, [driveInfo?.sensorInfos, metadata]);

  /// ///////////////////////////////////////////////////////////////////////////
  // Shortcuts
  /// ///////////////////////////////////////////////////////////////////////////
  const { shortcuts } = useStoreState((state) => ({ ...state.shortcut }));
  const { addShortcut } = useStoreActions((actions) => actions.shortcut);
  const { launchNewToast } = useStoreActions((actions) => actions.toast);
  // Keep currentFrameIdx inside a ref so we can use them in shortcuts
  // (set with useEffect) without updating the event listeners every
  // time
  const currentFrameIdxRef = useRef(0);
  useEffect(() => {
    currentFrameIdxRef.current = currentFrameIdx;
  }, [currentFrameIdx]);

  /// ///////////////////////////////////////////////////////////////////////////
  // Shortcuts Setup
  /// ///////////////////////////////////////////////////////////////////////////

  // --- 1. REFS (State Tracking) ---
  // Tracks where we want to be (ignores slow URL updates)
  const intendedFrameRef = useRef(currentFrameIdxRef.current);
  // Tracks when you last pressed a key (to block auto-play)
  const lastInputTimeRef = useRef(0);
  // Tracks direction: 1 (Forward), -1 (Backward), 0 (Idle)
  const scrollDirectionRef = useRef(0);
  // Tracks the last frame we successfully showed (for Worker filtering)
  const lastRenderedFrameRef = useRef(currentFrameIdxRef.current);

  // This will trigger the URL update 500ms AFTER you stop pressing keys.
  const debouncedUrlUpdate = useMemo(
    () =>
      debounce((frame: number) => {
        updateFrameInUrl(frame);
      }, 500),
    []
  );

  useEffect(() => {
    if (playing) return;

    // GUARD: If the user pressed a key recently (< 500ms),
    // DO NOT update the URL here. Let the debounce handler take care of it.
    // This prevents the "every frame" history update while holding keys.
    if (Date.now() - lastInputTimeRef.current < 500) return;

    updateFrameInUrl(currentFrameIdx);
  }, [playing, currentFrameIdx]);

  useEffect(() => {
    setShouldReplayCachedFrameOnRecreate(!playing);
  }, [playing, setShouldReplayCachedFrameOnRecreate]);

  const handleTogglePause = useCallback(() => {
    // If we're transitioning into play mode, clear manual-step state
    // immediately so worker message filtering cannot drop initial forward frames.
    if (!playing) {
      scrollDirectionRef.current = 0;
      lastInputTimeRef.current = 0;
      intendedFrameRef.current = currentFrameIdxRef.current;
    }
    togglePause();
  }, [playing, togglePause]);

  useEffect(() => {
    // Helper: Updates frame instantly & sets direction
    const safeUpdateFrame = (delta: number) => {
      setPlaying(false);

      const now = Date.now();
      // Reset if idle for >500ms (handles timeline clicks/jumps)
      if (now - lastInputTimeRef.current > 500) {
        intendedFrameRef.current = currentFrameIdxRef.current;
      }

      // Record Direction (-1 or 1) for the Worker to read
      scrollDirectionRef.current = delta > 0 ? 1 : -1;

      // Update local counters
      intendedFrameRef.current += delta;
      lastInputTimeRef.current = now;

      // Update Source of Truth
      // updateFrameInUrl(intendedFrameRef.current);
      setCurrentFrameIdx(intendedFrameRef.current);

      debouncedUrlUpdate(intendedFrameRef.current);
    };

    // Throttled Handlers
    const handlePreviousFrame = throttle(() => safeUpdateFrame(-1), 75);
    const handleNextFrame = throttle(() => safeUpdateFrame(1), 75);
    const handleRewind10Frames = throttle(() => safeUpdateFrame(-10), 75);
    const handleFastForward10Frames = throttle(() => safeUpdateFrame(10), 75);

    // Create all shortcuts with handlers
    const driveViewerShortcuts = createDriveViewerShortcuts({
      togglePause: handleTogglePause,
      increasePlaybackSpeed,
      decreasePlaybackSpeed,
      previousFrame: handlePreviousFrame,
      nextFrame: handleNextFrame,
      rewind10Frames: handleRewind10Frames,
      fastForward10Frames: handleFastForward10Frames,
      toggle2DImageVisibility,
      incrementPointSize: () => {
        const newSize = incrementPointSize();
        launchNewToast(`Point size: ${newSize}`);
      },
      decrementPointSize: () => {
        const newSize = decrementPointSize();
        launchNewToast(`Point size: ${newSize}`);
      },
      togglePointCloudAutoLevel: () => {
        try {
          const useLevels = togglePointCloudAutoLevel();
          launchNewToast(`Points auto-levels: ${useLevels ? 'ON' : 'OFF'}`);
        } catch (error) {
          if (error instanceof Error) {
            launchNewToast(`${error.message}`);
          } else {
            launchNewToast(`${error}`);
          }
        }
      },
      toggleCameraOrthographicMode: () => {
        const isOrthographic = toggleCameraOrthographicMode();
        launchNewToast(
          `Camera Type: ${isOrthographic ? 'Orthographic' : 'Perspective'}`
        );
      },
    });

    // Register all shortcuts
    driveViewerShortcuts.forEach((shortcut) => {
      addShortcut(shortcut);
    });

    return () => {
      handlePreviousFrame.cancel();
      handleNextFrame.cancel();
      handleRewind10Frames.cancel();
      handleFastForward10Frames.cancel();
    };
  }, [
    addShortcut,
    debouncedUrlUpdate,
    decreasePlaybackSpeed,
    decrementPointSize,
    handleTogglePause,
    increasePlaybackSpeed,
    incrementPointSize,
    launchNewToast,
    setCurrentFrameIdx,
    setPlaying,
    toggle2DImageVisibility,
    toggleCameraOrthographicMode,
    togglePointCloudAutoLevel,
  ]);

  const [osfPath, setOsfPath] = useState<string | null>(null);
  const [osfType, setOsfType] = useState<OSFType | null>(null);
  // For binary OSF we shift currentFrameIdx from global frame (i.e.
  // across all bags/osf files) to the local frame index that starts at
  // the current file bag/OSF (the current 'section')
  const [currentLocalFrameIdx, setLocalCurrentFrameIdx] = useState<
    number | null
  >(null);

  // Current offset to the local section (aka current osf file playing)
  const [localOffset, setLocalOffset] = useState<number>(0);
  const feedbackFormLink = useFeedbackForm();

  const [driveSignedUrl, setDriveSignedUrl] = useState<string>();
  useEffect(() => {
    if (!osfPath) {
      console.warn(`OSF path invalid: ${osfPath}`);
      return;
    }
    const osfDriveId = driveId ?? publishedDriveUuid;
    if (!osfDriveId) throw new Error(`Invalid osf drive id ${osfDriveId}`);

    const urlPromise = getSignedUrl(
      osfPath,
      osfDriveId,
      driveOrgId || curUserOrg?.id
    );
    urlPromise.then((url) => {
      setDriveSignedUrl(url);
    });
  }, [driveId, osfPath, publishedDriveUuid, driveOrgId, curUserOrg?.id]);

  // Update OSF path and type for every change of frame
  useEffect(() => {
    if (driveOsfInfo) {
      const [sectionId, localFrameIdx] = getSectionIdFromGlobalFrame(
        currentFrameIdx,
        driveOsfInfo
      );
      setOsfPath(extractOSFPath(driveOsfInfo, sectionId));
      const newOSFType = extractOSFType(driveOsfInfo, sectionId);
      setOsfType(newOSFType);
      setLocalOffset(extractOSFOffset(driveOsfInfo, sectionId));
      if (newOSFType === OSFType.osf) {
        setLocalCurrentFrameIdx(localFrameIdx);
      }
    } else {
      console.warn(`No drive osf info: ${driveOsfInfo}!`);
    }
  }, [driveOsfInfo, currentFrameIdx]);

  // Initialize viz datasource
  useEffect(() => {
    updateDataMessage(new Subject<DataMessage[]>());
  }, [updateDataMessage]);

  const pollProcesses = useCallback(() => {
    debounce(() => {
      if (driveId && !isUndefined(curUserOrg?.id)) {
        refreshDriveById({
          id: driveId,
          isPrivate: true,
          orgId: driveOrgId || curUserOrg?.id,
        });
      } else if (publishedDriveUuid) {
        refreshDriveById({
          id: publishedDriveUuid,
          isPrivate: false,
        });
      }
    }, DRIVE_REFRESH_DELAY);
  }, [
    driveId,
    publishedDriveUuid,
    refreshDriveById,
    driveOrgId,
    curUserOrg?.id,
  ]);

  // Load sensor metadata from OSF
  // TODO - can we start this earlier so load time is shortened?
  const setMetadataState = useCallback(async () => {
    if (!osfPath || !driveSignedUrl) {
      return;
    }
    // Consolidate, move this call into the OSF data source.
    try {
      const osf = await Osf(driveSignedUrl, window.fetch.bind(window));
      const sensorsMetadata = osf.getSensors() as Sensor[];
      updateMetadata(sensorsMetadata);
    } catch (error) {
      setHasVizError(true);
      if (error instanceof Error) {
        setVizErrorMessage(error.message);
      } else {
        setVizErrorMessage('We have an issue to construct the Osf file');
      }
    }
  }, [
    driveSignedUrl,
    osfPath,
    setHasVizError,
    setVizErrorMessage,
    updateMetadata,
  ]);

  useEffect(() => {
    setMetadataState();
  }, [setMetadataState]);

  /**
   * Refresh the drive info if any of the outputs are not finished
   */
  useEffect(() => {
    const loadFiles = async () => {
      try {
        if (driveId && !isUndefined(curUserOrg?.id)) {
          await loadOutputFileInfo({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
        } else if (publishedDriveUuid) {
          await loadOutputFileInfo({
            id: publishedDriveUuid,
            isPrivate: false,
          });
        }
      } catch (error) {
        showToast({
          title: `Failed to download recording outputs`,
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
      }
    };

    const allFinished =
      outputProcesses.length > 0 &&
      outputProcesses.every((o) =>
        [Status.Finished.toString(), Status.Failed.toString()].includes(
          o.generationStatus
        )
      );
    if (!allFinished) {
      // There are still processes running, Let's schedule a refresh of the
      // processes. Currently we have to reload the whole drive
      pollProcesses();
    } else if (
      allFinished &&
      filesToDownloadReady === false &&
      (driveId || publishedDriveUuid)
    ) {
      loadFiles();
    }
  }, [
    outputProcesses,
    driveId,
    publishedDriveUuid,
    filesToDownloadReady,
    loadOutputFileInfo,
    pollProcesses,
    driveOrgId,
    curUserOrg?.id,
    showToast,
  ]);

  // ============================
  // BEGIN: Worker integrations
  // ============================

  // Same creation pattern as StreamedFileVizProvider
  const worker = useMemo(() => {
    const wk = getOSFWorker();
    return wk;
  }, []);

  // Open-once-per-URL sentinel
  const openedRef = useRef(false);

  // last interaction + skip threshold remain as-is
  const lastUserInteractionTimeRef = useRef<number>(0);
  const AUTO_SKIP_THRESHOLD = 500;
  const isDraggingSeekRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);

  // Terminate on unmount (unchanged from your version, just grouped here)
  useEffect(
    () =>
      // Cleanup function to terminate the worker on component unmount
      () => {
        worker.terminate();
      },
    [worker]
  );

  // Worker message handler updated to also handle 'calculatedFps'
  worker.onmessage = (
    e: MessageEvent<
      | { frameIdx: number; dataMessages?: DataMessage[]; ts?: number }
      | { loadedFrames: number[] }
      | { framesCount: number; calculatedFps: number }
    >
  ) => {
    if (!e.data) return;

    if ('framesCount' in e.data) {
      const { framesCount } = e.data as { framesCount: number };
      if (Number.isFinite(framesCount) && framesCount > 0) {
        setNumFrames(framesCount);
      }
    }
    if ('calculatedFps' in e.data) {
      const { calculatedFps } = e.data as { calculatedFps: number };
      if (Number.isFinite(calculatedFps)) {
        setReplayFPS(calculatedFps);
      }
      return;
    }

    if (Object.hasOwn(e.data, 'loadedFrames')) {
      const msg = e.data as { loadedFrames: number[] };
      if (!isEqual(msg.loadedFrames, loadedLocalFrames)) {
        setLoadedLocalFrames(msg.loadedFrames);
      }
      return;
    }

    if (Object.hasOwn(e.data, 'frameIdx')) {
      const msg = e.data as {
        frameIdx: number;
        dataMessages?: DataMessage[];
        ts?: number;
      };

      // Prevents "Old" frames from overwriting "New" frames during lag
      const direction = scrollDirectionRef.current;
      const lastRendered = lastRenderedFrameRef.current;

      // Calculate if the keyboard was used recently (within 500ms).
      const isKeyboardActive = Date.now() - lastInputTimeRef.current < 500;

      // ONLY apply the filter if the keyboard is actively being used.
      // If the user clicked the Playbar or hit Play, 'isKeyboardActive' will be false,
      // so we skip this block and allow the frame to load immediately.
      if (!playing && isKeyboardActive && direction !== 0) {
        // Backward Filter
        if (direction === -1 && msg.frameIdx > lastRendered) return;

        // Forward Filter
        if (direction === 1 && msg.frameIdx < lastRendered) return;
      }

      // If accepted, update the tracker
      lastRenderedFrameRef.current = msg.frameIdx;
      // ===================================

      setlastReceivedFrameIdx(msg.frameIdx);
      if (msg.ts) setFrameTs(msg.ts);

      if (msg.dataMessages && Array.isArray(msg.dataMessages)) {
        // Cache the data for potential replay when settings change
        frameCache.lastDataMessage = msg.dataMessages;

        dataMessage.next(msg.dataMessages);
      } else if (
        !hasVizError &&
        // Ensure this check uses the same ref
        performance.now() - lastUserInteractionTimeRef.current >
          AUTO_SKIP_THRESHOLD
      ) {
        updateFrameInUrl(msg.frameIdx + 1);
      }
    }
  };

  // Open the OSF exactly once per new URL, then issue a 'get' for the current frame
  useEffect(() => {
    if (!window.Worker || !driveSignedUrl) return;
    if (!isVizInitialized) {
      return;
    }

    const recordingId = driveId || publishedDriveUuid;
    if (!openedRef.current) {
      worker.postMessage({
        command: 'open',
        signedOsfUrl: driveSignedUrl,
        bufferSize: 360,
        rate: 1,
        recordingId,
      });
      openedRef.current = true;
    }

    worker.postMessage({
      command: 'get',
      index: currentFrameIdx,
      preload: !seeking,
    });
  }, [
    currentFrameIdx,
    seeking,
    driveSignedUrl,
    isVizInitialized,
    worker,
    driveId,
    publishedDriveUuid,
  ]);

  const requestCurrentFrameWhenPaused = useMemo(
    () =>
      debounce((index: number) => {
        worker.postMessage({
          command: 'get',
          index,
          preload: false,
        });
      }, 50),
    [worker]
  );

  useEffect(() => {
    if (!playing) {
      return;
    }

    // Clear stale manual-step state so play mode does not inherit
    // backward/forward keyboard filtering decisions.
    scrollDirectionRef.current = 0;
    lastInputTimeRef.current = 0;
    intendedFrameRef.current = currentFrameIdxRef.current;
  }, [playing]);

  // While paused, re-request the current frame if visualization settings
  // change so rebuilt pipelines repaint without impacting playback throughput.
  useEffect(() => {
    if (
      !window.Worker ||
      !driveSignedUrl ||
      !openedRef.current ||
      !isVizInitialized ||
      playing
    ) {
      return;
    }

    requestCurrentFrameWhenPaused(currentFrameIdx);
  }, [
    availableChannels,
    currentFrameIdx,
    currentSensorImageSettings,
    driveSignedUrl,
    imageSettings,
    is2DImageVisible,
    isVizInitialized,
    playing,
    pointCloudGlobalSettings,
    pointCloudSensorsSettings,
    requestCurrentFrameWhenPaused,
  ]);

  // When URL changes, allow re-open
  useEffect(() => {
    openedRef.current = false;
  }, [driveSignedUrl]);

  // Poll for loaded frames 4x/s (unchanged)
  useAnimationFrameThrottled(() => {
    worker.postMessage({
      requestLoadedFrames: true,
    });
  }, 4);

  // ============================
  // END: Worker integrations
  // ============================

  /* 
    Due to the heavy work on rendering, replayFPS is an ideal rendering rate,
    but that is hard to achieve as is, it is impacted by the number of 2D image
    as well as the lidar mode. The higher resolution, and more 2D images will dramatically slow
    down the drawing speed. Hence, we have to apply some multiple to increase the drawing rate
    to match the desirable rate we want to have.
    However, it is still depended heavily on the hardware.
  */
  const adjustFrameRate = useMemo(
    () =>
      calculateAdjustedFrameRate(
        replayFPS,
        currentSensorImageSettings.length,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        lidarMode?.split('x')[0],
        playbackSpeed
      ),
    [replayFPS, currentSensorImageSettings.length, lidarMode, playbackSpeed]
  );

  // Loop to update frame index in URL
  // Current assumption is for single-osf drives only
  useAnimationFrameThrottled(() => {
    // GUARD: If user pressed a key recently (<500ms), STOP auto-play immediately.
    if (Date.now() - lastInputTimeRef.current < 500) {
      return;
    }

    const nextFrame =
      numFrames <= 1
        ? currentFrameIdx
        : mod(currentFrameIdx + 1, numFrames - 1);

    if (Number.isNaN(nextFrame)) return;

    const canAdvanceNormally =
      typeof lastReceivedFrameIdx === 'number' &&
      currentFrameIdx <= lastReceivedFrameIdx;
    const shouldBootstrapFromZero =
      currentFrameIdx === 0 &&
      (lastReceivedFrameIdx === undefined || lastReceivedFrameIdx > 0);

    if (playing && (canAdvanceNormally || shouldBootstrapFromZero)) {
      // updateFrameInUrl(nextFrame);
      setCurrentFrameIdx(nextFrame);
    }
  }, adjustFrameRate);

  const deleteImageHandler = useCallback(
    (sensorSn: string, index: number) => {
      deleteImage({ sensorSn, index });
    },
    [deleteImage]
  );

  const updateImageHandler = useCallback(
    (
      sensorSn: string,
      index: number,
      partialSettings: Partial<ImageSettings>
    ) => {
      updateImage({ sensorSn, index, partialSettings });
    },
    [updateImage]
  );

  const onCreateTag = useCallback(
    async (name: string) => {
      if (curUserOrg?.id) {
        try {
          const newTag = await createTag({
            organization_id: driveOrgId || curUserOrg.id,
            name,
          });
          return newTag as Tag;
        } catch (error) {
          showToast({
            title: 'Failed to add tag',
            description: `${error}`,
            status: 'error',
          });
        }
      }
    },
    [createTag, driveOrgId, curUserOrg?.id, showToast]
  );

  const handleTagsChange = useCallback(
    async (items: readonly (CreatedOption | MappedTag)[]) => {
      if (!curUserOrg?.id) return;
      if (items.length === 0) {
        updateTags({ newTags: [], orgId: driveOrgId || curUserOrg.id });
        return;
      }
      const tempTags: MappedTag[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for await (const tag of items) {
        if ('__isNew__' in tag) {
          // newly created tag, check whether it exists
          const existingTag = mappedTags.find(
            (t) => t.data.name === formatTag(tag.value)
          );
          if (existingTag) {
            tempTags.push(existingTag);
          } else {
            // If tag doesn't exist, create a new tag and add it
            // TODO (mw): This await causes a slight delay when creating a new tag
            const newTag = await onCreateTag(formatTag(tag.value));
            if (newTag) {
              // successful create
              // make equivalent mapped tag (name should already be formatted)
              // to add to selected tags
              const mappedNewTag = {
                label: newTag.name,
                value: newTag.name,
                data: newTag,
              };
              tempTags.push(mappedNewTag);
            }
          }
        } else {
          tempTags.push(tag);
        }
      }
      updateTags({
        newTags: uniq(tempTags),
        orgId: driveOrgId || curUserOrg.id,
      });
    },
    [driveOrgId, curUserOrg?.id, updateTags, mappedTags, onCreateTag]
  );

  /* 
    below are the function passed to left side panel
    wrap them in useCallback to avoid unnecessary render of panel during parent rendering.
    If we don't memo the side panel, it keeps redrawing itself during the play,
    which significantly affect the performance
  */
  const toggleFlipped = useCallback(() => {
    setFlipped(!flipped);
  }, [flipped, setFlipped]);

  const onRetract = useCallback(() => {
    setLeftSidebarExpanded(false);
  }, [setLeftSidebarExpanded]);

  const swapImagesHandler = useCallback(
    (sensorSn: string, indexA: number, indexB: number) => {
      swapImages({ sensorSn, indexA, indexB });
    },
    [swapImages]
  );

  const updatePointCloudSensorSettingsHandler = useCallback(
    (sensorName: string, newSettings: Partial<PointCloudSensorSettings>) => {
      updatePointCloudSensorsSettings([sensorName, newSettings]);
    },
    [updatePointCloudSensorsSettings]
  );

  const [isVizAlertShown, setIsVizAlertShown] = useState(false);
  const [hasVizAlert, setHasVizAlert] = useState(false);
  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();
  useEffect(() => {
    if (hasVizAlert && !isVizAlertShown) {
      onDialogOpen();
    }
  }, [hasVizAlert, isVizAlertShown, onDialogOpen]);
  const handleModalClose = () => {
    onDialogClose();
    setIsVizAlertShown(true);
  };
  useEffect(() => {
    if (driveInfo?.visualization_status === 'NOT_VIEWABLE' && !hasVizAlert) {
      setHasVizAlert(true);
    }
  }, [driveInfo?.visualization_status, hasVizAlert]);

  const hasRgbChannel =
    !availableChannels || availableChannels.length === 0
      ? true // Assume "rgb" is present if no data
      : rgbChannels && rgbChannels.length > 0;

  // This portion of code ensures the state of the frame index in the
  // reducer is in sync with the URL. Making the URL the 'source of
  // truth' simplifies things here and lets the user change the frame
  // directly by updating the URL. This piece of code should be the only
  // one calling setCurrentFrameIdx
  const prevRouteFrameIdx = usePrevious(routeFrameIdx);
  useEffect(() => {
    if (routeFrameIdx !== undefined && prevRouteFrameIdx !== routeFrameIdx) {
      setCurrentFrameIdx(routeFrameIdx);
    }
  }, [routeFrameIdx, prevRouteFrameIdx, setCurrentFrameIdx]);

  // Track when OSF processing failed
  useSentryErrorTracker(
    driveInfo?.osfGenerationStatus === Status.Failed,
    'OSF generation failed during data processing',
    'DriveViewer (OSF Failed)',
    { driveId, publishedDriveUuid }
  );

  // Track when we have a legacy OSF that cannot be visualized
  useSentryErrorTracker(
    osfType === OSFType.legacyOsf,
    'Data was processed into a legacy OSF format that can no longer be visualized',
    'DriveViewer (Legacy OSF)',
    { driveId, publishedDriveUuid }
  );

  // Track when we have a viz error
  useSentryErrorTracker(
    hasVizError,
    vizErrorMessage || 'Unknown visualization error',
    'DriveViewer (Viz Error)',
    { driveId, publishedDriveUuid }
  );

  useSentryErrorTracker(
    loadStatus === 'failed' || !driveInfo,
    'Drive load failed: Missing drive information or load error',
    'DriveViewer (Drive Load Error)',
    { driveId, publishedDriveUuid }
  );

  const isVizReady = vizPageRef.current?.getIsDrawn
    ? vizPageRef.current.getIsDrawn()
    : false;

  const getGeneralErrorMessage = () => {
    if (isPublished || publishedDriveUuid) {
      // if this a published drive, user can't see it means the drive is deleted or unpublished
      return (
        <Box lineHeight="2rem" data-testid="published-drive-not-found">
          Unable to load the dataset. This may be due to the resource being
          deleted or corrupted. Please contact the person who shared the link
          with you.
        </Box>
      );
    }

    const hasViewingPermission = userOrgs.some((org) => org.id === driveOrgId);

    if (!hasViewingPermission) {
      // when user logged in but not a member of the org, they will see this error message
      // Fyi, for supported ouster user, they will be able to load the driveInfo, so they will not see this error message
      return (
        <Box lineHeight="2rem" data-testid="not-org-member-error">
          You are not a member of the organization this recording belongs to.
          Please ask that organization to invite you to see this recording.
        </Box>
      );
    }

    // when it is not related to the permission, user supposed to see the recording
    // but for some reason, we can't load the driveInfo, then we show this error message
    return (
      <Box data-testid="general-error-message">
        <Box lineHeight="2rem">
          Unable to load the dataset. This may be due to the resource being
          deleted or corrupted. Please go back to the list page and try again.
        </Box>
        <Box textAlign="center">
          <Button
            as={Link}
            to="/drive/private"
            variant="solid"
            data-testid="return-to-list-button"
          >
            Return to List page
          </Button>
        </Box>
        <Box>
          If you continue to experience issues, please reach out to us via this
          form:
        </Box>
        <Box textAlign="center">
          <Button
            as={Link}
            to={feedbackFormLink}
            variant="outline"
            data-testid="feedback-form-button"
          >
            Feedback Form
          </Button>
        </Box>
      </Box>
    );
  };

  if (loadStatus === 'loading' || loadStatus === 'notStarted') {
    return (
      /* 
        Use the consistent loading animation
      */
      <LoaderContainer>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </LoaderContainer>
    );
  }

  if (loadStatus === 'failed' || !driveInfo) {
    return (
      <Box
        m="auto"
        fontSize={18}
        display="grid"
        rowGap="2rem"
        maxWidth="60ch"
        textAlign="center"
        data-testid="general-error-container"
      >
        {getGeneralErrorMessage()}
      </Box>
    );
  }

  /* 
    Handle general viz error from ouster lib like viz, data source and osf
    the message from these lib will be print out and keep the nav bar so user can still download recording file
  */
  if (hasVizError) {
    return (
      <>
        <DriveNavBar orgId={driveOrgId} hasRGBChannels={hasRgbChannel} />
        <ContainerBelowNav>
          <VizInfoMessageContainer>
            <div data-testid="viz-error-container">
              We encountered an issue while trying to visualize your recording.
              The error message is as follows:{' '}
              <em id="errorMessageViz">{vizErrorMessage}</em>
              {'\n'}
              {'\n'}
              To investigate further, you can download the recording through the
              download button located on the top right. If needed, you can also
              reach out to the Ouster Studio Web team for assistance by filling
              out{' '}
              <FeedbackFormContainer formLink={feedbackFormLink}>
                this form
              </FeedbackFormContainer>
              .
            </div>
          </VizInfoMessageContainer>
        </ContainerBelowNav>
      </>
    );
  }

  // Webviz expects a single URL, so we point to the first OSF directory.
  // To point to images from other bags in other directories, we rewrite the
  // requested URL.
  let webvizEl: JSX.Element | null = null;

  // In first 3 if blocks below, we display customized info messages to
  // users to when
  //   1. OSFs are being processed
  //   2. OSFs processing failed
  //   3. OSFs are in a legacy format we can no longer visualize
  if (
    driveInfo.osfGenerationStatus === Status.NotStarted ||
    driveInfo.osfGenerationStatus === Status.Running
  ) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="osf-processing-message">
          We are still processing your data so you can visualize it. Please
          check back here later.{'\n'}
          {'\n'}
          If your data is still processing a couple of hours after you uploaded
          it, you can reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>{' '}
          to have them investigate.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (driveInfo.osfGenerationStatus === Status.Failed) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="osf-failed-message">
          Your data cannot be visualized because an error occurred during its
          processing.{'\n'}
          {'\n'}
          Please reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>{' '}
          to have them diagnose the issue further.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (osfType === OSFType.legacyOsf) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="legacy-osf-message">
          This data was initially processed into a legacy format that we can no
          longer visualize.{'\n'}
          {'\n'}Please reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>
          to ask them to re-process it.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (
    osfPath !== null &&
    currentLocalFrameIdx !== null &&
    // With new changes, we can only display OSF V2
    osfType === OSFType.osf &&
    // There needs to be an id in the URL
    (driveId !== null || publishedDriveUuid != null)
  ) {
    if (!metadata || isEmpty(metadata)) {
      return (
        /* 
          Use the consistent loading animation
        */
        <LoaderContainer>
          <CircularProgress isIndeterminate size="50px" thickness={10} />
        </LoaderContainer>
      );
    }
    const {
      dataCategory,
      uploadedBy,
      processedAt,
      startTime,
      sourceBucket,
      sourcePrefix,
      sensorInfos: driveSensorInfos,
    } = driveInfo;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const sensors: SensorSectionInfo[] = metadata.map(
      (singleSensorMetadata: any, index: number) => {
        const {
          sensorInfo: mdSensorInfo = {}, // Use alias to avoid naming conflict
          configParams = {},
          dataFormat = {},
          lidarMode: mdLidarMode,
        } = singleSensorMetadata;
        const {
          prodSn: mdProductSn,
          status: mdStatus,
          buildRev: mdBuildRev,
          imageRev: mdImageRev,
          hwRev: mdHwRev,
        } = mdSensorInfo; // Get productSn from metadata
        const { azimuthWindow, signalMultiplier } = configParams;

        // Find corresponding driveInfo sensor data by matching serial numbers
        let driveSensorInfo = driveSensorInfos?.find(
          (dsi) => dsi.sdkProductSerialNumber === mdProductSn
        );
        // If no match found, try falling back to the first item if available
        if (
          !driveSensorInfo &&
          driveSensorInfos &&
          driveSensorInfos.length > 0
        ) {
          // eslint-disable-next-line prefer-destructuring
          driveSensorInfo = driveSensorInfos[0];
        }

        if (!driveSensorInfo) {
          // Handle error appropriately, maybe return loading or error state
          console.error(
            'Failed to create sensor info from metadata and driveInfo.'
          );
          return {
            info: {
              sn: `unknown-${index}`,
              prodLine: '',
              prodPn: '',
              buildRev: '',
              imageRev: '',
              status: 'UNKNOWN',
              beamSpacing: undefined,
            },
            config: {
              lidarMode: '',
              udpProfileLidar: '',
              azimuthWindow: [0, 360000],
              signalMultiplier: undefined,
            },
          };
        }

        // Now destructure from the matched driveSensorInfo
        const {
          sdkProductSerialNumber, // This should match mdProductSn if found
          sdkProductType,
          sdkProductResolution,
          sdkProductPartNumber,
          sdkProductBeamSpacing,
          sdkFwBuildRev,
          sdkFwImageRev,
          sdkFwVersionCore,
          sdkProductHwRevision,
          sdkLidarMode, // Get lidarMode per sensor if available
        } = driveSensorInfo;

        // Resolve lidar mode for this specific sensor
        const currentLidarMode =
          configParams?.lidarMode ?? sdkLidarMode ?? mdLidarMode;
        // Resolve UDP profile for this specific sensor
        const currentUdpProfileLidar =
          configParams?.udpProfileLidar ?? dataFormat?.udpProfileLidar ?? '';

        const sensorConfig: SensorConfig = {
          lidarMode: currentLidarMode,
          udpProfileLidar: currentUdpProfileLidar,
          azimuthWindow: azimuthWindow ?? [0, 360000],
          signalMultiplier,
        };

        const si: SensorInfo = {
          sn: mdProductSn ?? sdkProductSerialNumber ?? `unknown-${index}`,
          prodLine:
            sdkProductType && sdkProductResolution
              ? `${sdkProductType} - ${sdkProductResolution}`
              : '',
          prodPn: sdkProductPartNumber ?? '',
          buildRev: sdkFwVersionCore ?? sdkFwBuildRev ?? mdBuildRev ?? '',
          imageRev: sdkFwImageRev ?? mdImageRev ?? '',
          hwRev: sdkProductHwRevision ?? mdHwRev ?? '',
          status: mdStatus ?? 'UNKNOWN', // Use status from metadata
          beamSpacing: sdkProductBeamSpacing ?? undefined,
        };

        return { info: si, config: sensorConfig };
      }
    );

    const filesInfo: FilesInfo = {
      files: sourceFileInfo.map((fileInfo) => {
        const { filename } = fileInfo;
        // Default to original filename
        let name = filename;

        // If recordingTitle is not available, keep original name
        const recTitle = driveInfo?.recordingTitle;

        // Leave extrinsic_parameters.json unchanged
        if (filename === 'extrinsic_parameters.json' || !recTitle) {
          return { name };
        }

        const lastDot = filename.lastIndexOf('.');
        const ext = lastDot !== -1 ? filename.substring(lastDot) : '';
        const base = lastDot !== -1 ? filename.substring(0, lastDot) : filename;

        if (ext.toLowerCase() === '.json') {
          // For json files (except extrinsic_parameters.json), keep suffix after last '_' and prepend recordingTitle
          const lastUnderscore = base.lastIndexOf('_');
          const suffix =
            lastUnderscore !== -1 ? base.substring(lastUnderscore + 1) : base;
          name = `${recTitle}_${suffix}${ext}`;
        } else {
          // For other files keep the extension but replace the base name with recordingTitle
          name = `${recTitle}${ext}`;
        }

        return { name };
      }),
      recordedOn: startTime === '...' ? undefined : startTime,
      uploadedOn: processedAt,
      uploadedBy: uploadedBy ?? undefined,
      dataCategory: dataCategory ?? undefined,
      // Only show gcs link if ouster user
      sourceDir: isOusterUser
        ? `${GCS_BASE_URL}/${sourceBucket}/${sourcePrefix}`
        : undefined,
    };
    const streamsInfo: ReplayStreamInfo = {
      files: filesInfo,
      sensors,
    };

    const sidebarWidth = '22.5rem';
    const toggleButtonProps: Omit<ComponentProps<typeof Button>, 'children'> = {
      variant: 'ghost',
      emphasis: 'subtle',
      color: 'content-main',
      size: 'md',
      position: 'absolute',
      top: '0.25rem',
      backgroundColor: 'background-surface-subtle',
      transitionProperty: 'opacity',
      transitionDuration: 'normal',
      tight: true,
      border: 'sm',
      borderColor: 'border-divider',
      zIndex: 'viz-area-overlay-2',
      gap: '0.25rem',
      pr: '0.5rem',
    };

    webvizEl = (
      <Box
        display="grid"
        gridTemplateRows="1fr auto auto"
        height="100%"
        overflow="hidden"
      >
        <Flex
          transitionProperty="margin"
          transitionDuration="normal"
          transitionTimingFunction="ease-in-out"
          marginLeft={leftSidebarExpanded ? '0' : `-${sidebarWidth}`}
          position="relative"
          flex="1 1 auto"
          height="100%"
          overflow="hidden"
          flexDirection="row"
        >
          {/* Button to expand the sidebar */}
          <Button
            {...toggleButtonProps}
            left={sidebarWidth}
            onClick={() => {
              setLeftSidebarExpanded(!leftSidebarExpanded);
            }}
            borderLeftRadius={0}
            opacity={leftSidebarExpanded ? 0 : 1}
            borderTop="0"
            transformOrigin="top left"
            transform="rotate(-90deg) translateX(-100%)"
            zIndex={100}
            data-testid="expand-left-sidebar-button"
          >
            Scene
            <Icon icon="keyboardDoubleArrowLeft" transform="rotate(90deg)" />
          </Button>
          {/* 
            Previously, there are many inline functions passed as props to VizLeftSidebar
            Every time, when parent re-render, a new function will be created and passed
            to VizLeftSidebar, cause VizLeftSidebar make unnecessary re-rendering.
            To improve the playback speed, we memorize the component as well as these functions.
          */}
          <VizLeftSidebar
            key={JSON.stringify(availableChannels)}
            pointCloudSettings={{
              global: pointCloudGlobalSettings,
              sensors: pointCloudSensorsSettings,
            }}
            availableChannels={availableChannels}
            show2DImage={is2DImageVisible}
            updatePointCloudGlobalSettings={updatePointCloudGlobalSettings}
            updatePointCloudSensorSettings={
              updatePointCloudSensorSettingsHandler
            }
            streamsInfo={streamsInfo}
            processesInfo={
              isOusterUser // Hide data source links for external users
                ? outputProcesses
                : outputProcesses.map((process) => {
                    const restrictedProcess = process;
                    restrictedProcess.bucket = null;
                    restrictedProcess.prefix = null;
                    return restrictedProcess;
                  })
            }
            on2DImageVisibilityChange={toggle2DImageVisibility}
            onPointCloudVisibilityChange={togglePointCloudVisibility}
            flexShrink={0}
            ref={leftSidebarRef}
            onRetract={onRetract}
            imageInfo={imageInfo}
            imageSettings={imageSettings}
            updateImage={updateImageHandler}
            deleteImage={deleteImageHandler}
            addImage={addImage}
            swapImages={swapImagesHandler}
            tags={
              publishedDriveUuid || disableTagRef.current
                ? undefined
                : mappedSelectedTags
            }
            allTagOptions={
              publishedDriveUuid || disableTagRef.current
                ? undefined
                : mappedTags
            }
            onTagsChange={handleTagsChange}
            formatTag={formatTag}
            isTagDisabled={isSample && curUserOrg?.role !== 'ADMIN'}
            flipped={flipped}
            toggleFlipped={toggleFlipped}
            showAngleRulers={showImageRulers}
            toggleAngleRulers={toggleAngleRulersVisibility}
            maxStackedImages={3}
            isOuster={isOusterUser}
            selectedImageSensorSn={selectedImagePanelSn ?? ''}
            on2DImageSensorChange={setSelectedImagePanelSn}
            hoverMode={hoverMode}
            setHoverMode={setHoverMode}
            paused={!playing}
            isVizReady={isVizReady}
            rgbChannels={rgbChannels}
          />
          <Flex flexGrow={1}>
            <VizPage
              ref={vizPageRef}
              driveInfo={driveInfo}
              pointSize={pointSize ?? 5}
              flipped={flipped}
              pointCloudGlobalSettings={pointCloudGlobalSettings}
              pointCloudSensorsSettings={pointCloudSensorsSettings}
              paused={!playing}
              hoverMode={hoverMode}
              setHoverMode={setHoverMode}
              onVizInitialized={setIsVizInitialized}
            />
          </Flex>
        </Flex>
        <SliderControlsContainer>
          <Box
            paddingRight="48px"
            width="100%"
            backgroundColor="background-surface-strong"
          >
            <PlayBar
              startIdx={frameStartIdx}
              endIdx={numFrames - 1}
              curIdx={currentFrameIdx}
              rate={1}
              togglePause={handleTogglePause}
              paused={!playing}
              setIdx={(index: number) => {
                if (!Number.isNaN(index)) {
                  // Used for forward/back frame buttons
                  // Want to throttle these calls as well
                  onSliderChange([index]);
                }
              }}
              maxLoadedIdx={
                getMaxLoadedLocalFrame(
                  currentLocalFrameIdx,
                  loadedLocalFrames
                ) + localOffset
              }
              playbackSpeed={playbackSpeed}
              playbackSpeedOptions={playbackSpeedOptions} // Reverse order so top option is highest multiplier
              setPlaybackSpeed={(speed: number) => {
                setPlaybackSpeed(speed);
              }}
              isIndexing={false}
              pctIndexed={100}
              onSliderChange={(values) => {
                if (!isDraggingSeekRef.current) {
                  isDraggingSeekRef.current = true;
                  wasPlayingBeforeSeekRef.current = playing;
                  if (playing) {
                    setPlaying(false);
                  }
                }

                const sliderValue = Math.floor(values[0]);
                if (Number.isNaN(sliderValue)) return;

                lastUserInteractionTimeRef.current = performance.now();
                if (!seeking) setSeeking(true);
                onSliderChange(values);
              }}
              onSliderMouseUp={(values) => {
                const sliderValue = Math.floor(values[0]);
                lastUserInteractionTimeRef.current = performance.now();
                onSliderChange.cancel(); // Cancel trailing seek calls
                setSeeking(false);
                if (!Number.isNaN(sliderValue)) {
                  setCurrentFrameIdx(sliderValue);
                  updateFrameInUrl(sliderValue);
                }
                isDraggingSeekRef.current = false;
                if (wasPlayingBeforeSeekRef.current) {
                  setPlaying(true);
                }
                wasPlayingBeforeSeekRef.current = false;
              }}
              curTs={frameTs}
              fps={replayFPS}
            />
          </Box>
        </SliderControlsContainer>
        <VizFooter mode="exploring" py="1em" />
        <ModalContainer
          isOpen={isDialogOpen}
          onClose={handleModalClose}
          header="Visualization Alert"
          footer={null}
          content={
            <p style={{ maxWidth: '500px', lineHeight: '2rem' }}>
              We encountered an error while processing your recording.
              We&apos;ve done our best to recover it and help you visualize it,
              but it still might not work as expected. If you&apos;re unable to
              view your recording, feel free to share it with our support team,
              and we&apos;ll look into it.
            </p>
          }
        />
      </Box>
    );
  } else {
    webvizEl = (
      <LoaderContainer>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </LoaderContainer>
    );
  }

  return (
    <>
      <DriveNavBar orgId={driveOrgId} hasRGBChannels={hasRgbChannel} />
      <ContainerBelowNav>
        <TopLevel>
          <ShortcutKeyHandler shortcuts={shortcuts} isMac={isMacOs} />
          {webvizEl}
        </TopLevel>
      </ContainerBelowNav>
      <Toast />
    </>
  );
});

export { DriveViewer };
